import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { APPLICATION_URL, boatPickerCopy, boats } from "../../content";
import { useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";
import { Button } from "../ui/Button";
import { BoatLine } from "../art/BoatLine";

/* Shipping-tag tilt cycle; hover/focus/selection straightens the tag. */
const tilts = ["rotate-[-2deg]", "rotate-[1.5deg]", "rotate-[-1deg]", "rotate-[2.5deg]"];

export function BoatPicker() {
  const reduce = useReducedMotion();
  const { boatId, pickBoat, stamp } = useGame();
  const selected = boats.find((b) => b.id === boatId) ?? null;

  return (
    <section id="boats" className="bg-paper py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <Waypoint station="boat" title={boatPickerCopy.title} sub={boatPickerCopy.sub} />

        <div className="mt-12 grid gap-10 lg:grid-cols-[1.55fr_1fr] lg:items-start">
          <div
            role="group"
            aria-label={boatPickerCopy.title}
            className="grid grid-cols-2 gap-4 md:grid-cols-3"
          >
            {boats.map((boat, i) => {
              const isSelected = boat.id === boatId;
              return (
                <motion.button
                  key={boat.id}
                  type="button"
                  onClick={() => {
                    pickBoat(boat.id);
                    stamp("boat");
                  }}
                  aria-pressed={isSelected}
                  initial={reduce ? false : { opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.55, delay: (i % 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
                  className={`group relative min-h-28 cursor-pointer rounded-md border-2 border-ink p-4 text-left transition-[translate,rotate,box-shadow,background-color,color] duration-200 ease-out hover:-translate-y-1 hover:rotate-0 hover:shadow-lift focus-visible:rotate-0 active:translate-y-0 motion-reduce:hover:translate-y-0 ${
                    isSelected
                      ? "rotate-0 bg-ink text-foam shadow-lift"
                      : `bg-foam text-ink shadow-card ${tilts[i % tilts.length]}`
                  }`}
                >
                  {/* punched tag eyelet */}
                  <span
                    aria-hidden
                    className={`absolute top-3 left-3 block size-3 rounded-full border-2 ${
                      isSelected ? "border-foam/70 bg-ink" : "border-ink bg-paper"
                    }`}
                  />
                  <span className="mt-4 block leading-tight font-semibold md:text-lg">
                    {boat.label}
                  </span>
                  <span
                    className={`mt-1.5 hidden text-sm transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100 md:block ${
                      isSelected ? "text-foam/70 opacity-100" : "text-muted opacity-0"
                    }`}
                  >
                    {boat.line}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {/* The dock board answers back once you pick. */}
          <div aria-live="polite" className="lg:sticky lg:top-24">
            <AnimatePresence mode="wait" initial={false}>
              {selected ? (
                <motion.div
                  key={selected.id}
                  initial={reduce ? false : { opacity: 0, scale: 0.96, y: 14 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={reduce ? undefined : { opacity: 0, scale: 0.98, y: -10 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="rotate-[0.6deg] rounded-md bg-ink p-8 text-foam shadow-lift"
                >
                  <p className="font-mono text-sm text-foam/70">{selected.reply}</p>
                  <p className="hand-note mt-4 text-foam">{selected.line}</p>
                  <p className="mt-6 text-foam/80">{boatPickerCopy.afterReply}</p>
                  <div className="mt-8 border-t border-foam/20 pt-6">
                    <p className="font-semibold">{boatPickerCopy.ctaQuestion}</p>
                    <Button href={APPLICATION_URL} variant="night" className="mt-4">
                      {boatPickerCopy.ctaLabel}
                    </Button>
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  key="empty"
                  initial={reduce ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduce ? undefined : { opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="flex min-h-[22rem] items-center justify-center rounded-md border-2 border-dashed border-line bg-foam"
                >
                  <BoatLine className="w-36 text-line" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        <p className="mt-12 text-sm text-muted">{boatPickerCopy.fineprint}</p>
      </div>
    </section>
  );
}
